// Pure helpers behind Predict's Model and scoring page (STRY0039090): how the
// scores on the customer's watch list are spread, and the weakness types and
// scorer versions of its bugs. Everything comes from GET /predict/watchlist,
// whose rows carry each bug's weakness type and scorer version since
// STRY0039096; from an ODD stage without that, each bug's come from
// GET /predict/bug instead.

import { PROMOTION_SCORE, weaknessLabel } from './predictWatchlistModel';

export const BIN_WIDTH = 0.5;
export const MAX_SCORE = 10;

// The bug details read for the breakdowns when the list doesn't carry them,
// the list's highest scores first. There is no batch read, so each is one
// GET /predict/bug; the cap keeps a visit to a couple of dozen calls however
// long the list grows.
export const DETAIL_LIMIT = 25;

const has = (bug, field) => Object.prototype.hasOwnProperty.call(bug || {}, field);

// Whether the list's own rows carry each bug's weakness type and scorer version
// (ODD sends both, null when not recorded, since STRY0039096), so the
// breakdowns can count the whole list with no detail reads.
export const listCarriesDetails = bugs =>
  bugs.length > 0 && bugs.every(bug => has(bug, 'weaknessType') && has(bug, 'scorerVersion'));

const isScore = score => typeof score === 'number' && Number.isFinite(score);

// To one decimal, as the page shows it, so a bug sits in the bin its shown score names.
const shown = score => Math.round(score * 10) / 10;

// Half-point bins from the lowest of 6.0, the threshold and the lowest score
// up to 10.0: [{from, to, count}], `to` exclusive except for the last bin.
// Every bug with a score lands in exactly one bin, so the bins add up to the
// list; `unscored` counts the rest.
export const scoreHistogram = (bugs, threshold = null) => {
  const scores = bugs
    .map(bug => bug?.score)
    .filter(isScore)
    .map(shown);
  const floors = [PROMOTION_SCORE, ...(isScore(threshold) ? [threshold] : []), ...scores];
  const start = Math.max(0, Math.floor(Math.min(...floors) / BIN_WIDTH) * BIN_WIDTH);
  const count = Math.max(1, Math.round((MAX_SCORE - start) / BIN_WIDTH));
  const bins = Array.from({ length: count }, (_, i) => ({
    from: Number((start + i * BIN_WIDTH).toFixed(1)),
    to: Number((start + (i + 1) * BIN_WIDTH).toFixed(1)),
    count: 0
  }));
  scores.forEach(score => {
    const i = Math.min(count - 1, Math.max(0, Math.floor((score - start + 1e-9) / BIN_WIDTH)));
    bins[i].count += 1;
  });
  return { bins, scored: scores.length, unscored: bugs.length - scores.length };
};

// A bin's label: '6.5–6.9', and '9.5–10.0' for the last one.
export const binLabel = (bin, last = false) => `${bin.from.toFixed(1)}–${(last ? bin.to : bin.to - 0.1).toFixed(1)}`;

const tally = (values, label) => {
  const counts = new Map();
  values.forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: label(key), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

const NONE = '';

// The scorer's defect classes across the bugs read, most first: [{key, label, count}].
// A bug with no weakness type counts as 'Not recorded'.
export const weaknessBreakdown = details =>
  tally(
    details.map(bug => (typeof bug?.weaknessType === 'string' && bug.weaknessType.trim() ? bug.weaknessType : NONE)),
    key => (key === NONE ? 'Not recorded' : weaknessLabel(key))
  );

// The scorer versions that scored the bugs read, most first: [{key, label, count}].
export const scorerVersions = details =>
  tally(
    details.map(bug =>
      typeof bug?.scorerVersion === 'string' && bug.scorerVersion.trim() ? bug.scorerVersion.trim() : NONE
    ),
    key => (key === NONE ? 'Not recorded' : key)
  );

// Runs `task` on each item, at most `limit` at a time, and resolves to
// [{item, ok, value | error}] in the items' order. `onSettled(done)` hears each
// one finish; `isCancelled()` stops new tasks from starting.
export const runLimited = async (items, limit, task, { onSettled = () => {}, isCancelled = () => false } = {}) => {
  const results = new Array(items.length);
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < items.length && !isCancelled()) {
      const index = next;
      next += 1;
      try {
        results[index] = { item: items[index], ok: true, value: await task(items[index]) };
      } catch (error) {
        results[index] = { item: items[index], ok: false, error };
      }
      done += 1;
      onSettled(done);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return results.filter(Boolean);
};
